// src/orders/parse-csv.ts
import Papa from "papaparse";
var REQUIRED_HEADERS = [
  "patient_name",
  "patient_phone",
  "address_line_1",
  "city",
  "state",
  "zip",
  "order_number"
];
var OPTIONAL_HEADERS = ["address_line_2", "delivery_notes", "is_cold_chain"];
var ALL_KNOWN_HEADERS = /* @__PURE__ */ new Set([
  ...REQUIRED_HEADERS,
  ...OPTIONAL_HEADERS
]);
var US_STATE_CODES = /* @__PURE__ */ new Set([
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
  "DC"
]);
function decodeUtf8Strict(buffer) {
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(buffer);
  } catch {
    return null;
  }
}
function isWhitespaceOnly(value) {
  return value === void 0 || value.trim() === "";
}
function isValidZip(zip) {
  return /^\d{5}$/.test(zip) || /^\d{5}-\d{4}$/.test(zip);
}
function parseBoolCell(raw) {
  const norm = raw.trim().toLowerCase();
  if (norm === "") return false;
  if (norm === "true" || norm === "1" || norm === "t" || norm === "yes" || norm === "y") {
    return true;
  }
  if (norm === "false" || norm === "0" || norm === "f" || norm === "no" || norm === "n") {
    return false;
  }
  return null;
}
function isValidNanpTenDigit(digits) {
  if (digits.length !== 10) return false;
  const areaFirst = digits[0];
  const exchFirst = digits[3];
  if (areaFirst < "2" || areaFirst > "9") return false;
  if (exchFirst < "2" || exchFirst > "9") return false;
  return true;
}
function normalizeUsPhoneToE164(raw) {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const digitsOnly = trimmed.replace(/\D/g, "");
  let national = digitsOnly;
  if (trimmed.startsWith("+")) {
    if (trimmed.startsWith("+1") && digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
      national = digitsOnly.slice(1);
    } else if (trimmed.startsWith("+1") && digitsOnly.length === 10) {
      national = digitsOnly;
    } else {
      return null;
    }
  } else if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
    national = digitsOnly.slice(1);
  } else if (digitsOnly.length === 10) {
    national = digitsOnly;
  } else {
    return null;
  }
  if (!isValidNanpTenDigit(national)) return null;
  return `+1${national}`;
}
function firstNonEmptyLine(text) {
  const lines = text.split(/\r?\n/);
  for (const line of lines) {
    if (line.trim() !== "") return line;
  }
  return null;
}
function nonCommaDelimiterError(firstLine) {
  if (!/[\t|;]/.test(firstLine)) return null;
  const commaSeparatedParts = firstLine.split(",").length;
  if (commaSeparatedParts > 1) return null;
  return "CSV must use comma as the delimiter (tab, pipe, and semicolon-separated files are not supported).";
}
function buildHeaderIndex(headerCells) {
  const index = /* @__PURE__ */ new Map();
  const warnings = [];
  const extras = [];
  for (let c = 0; c < headerCells.length; c++) {
    const key = headerCells[c].trim().toLowerCase();
    if (key === "") continue;
    if (!ALL_KNOWN_HEADERS.has(key)) {
      extras.push(headerCells[c].trim());
      continue;
    }
    if (index.has(key)) {
      return { ok: false, file_errors: [`Duplicate column in header: ${key}`] };
    }
    index.set(key, c);
  }
  if (extras.length > 0) {
    warnings.push(`Ignored unknown CSV columns: ${extras.join(", ")}`);
  }
  for (const req of REQUIRED_HEADERS) {
    if (!index.has(req)) {
      return {
        ok: false,
        file_errors: [`Missing required column: ${req}`]
      };
    }
  }
  return { ok: true, index, warnings };
}
function papaFileLevelErrors(errors) {
  const out = [];
  for (const e of errors) {
    if (e.type === "Quotes") {
      out.push(e.message || "CSV could not be parsed (quoted field error).");
    }
  }
  return out;
}
function parseOrdersCsv(buffer) {
  const text = decodeUtf8Strict(buffer);
  if (text === null) {
    return { ok: false, file_errors: ["File is not valid UTF-8."] };
  }
  if (text.trim() === "") {
    return { ok: false, file_errors: ["CSV file is empty."] };
  }
  const firstLine = firstNonEmptyLine(text);
  if (!firstLine) {
    return { ok: false, file_errors: ["CSV file is empty."] };
  }
  const delimErr = nonCommaDelimiterError(firstLine);
  if (delimErr) {
    return { ok: false, file_errors: [delimErr] };
  }
  const parsed = Papa.parse(text, {
    delimiter: ",",
    header: false,
    // Keep blank logical rows so `row_number` matches RFC 4180 record indices (spec §5).
    skipEmptyLines: false
  });
  const quoteErrors = papaFileLevelErrors(parsed.errors);
  if (quoteErrors.length > 0) {
    return { ok: false, file_errors: quoteErrors };
  }
  const rows = parsed.data;
  if (rows.length === 0) {
    return { ok: false, file_errors: ["CSV file is empty."] };
  }
  const headerCells = rows[0].map((cell) => (cell ?? "").trim());
  if (headerCells.every((h) => h === "")) {
    return { ok: false, file_errors: ["CSV header row is missing or empty."] };
  }
  const headerBuilt = buildHeaderIndex(headerCells);
  if (!headerBuilt.ok) {
    return { ok: false, file_errors: headerBuilt.file_errors };
  }
  const { index: col, warnings } = headerBuilt;
  const headerWidth = headerCells.length;
  const invalid = [];
  const valid = [];
  const seenOrderNumbers = /* @__PURE__ */ new Set();
  for (let i = 1; i < rows.length; i++) {
    const rowNumber = i + 1;
    const row = rows[i];
    const errors = [];
    if (row.every((cell) => (cell ?? "").trim() === "")) {
      continue;
    }
    const get = (name) => {
      const idx = col.get(name);
      if (idx === void 0) return "";
      return (row[idx] ?? "").trim();
    };
    if (row.length < headerWidth) {
      errors.push("Row has fewer fields than the header row.");
    }
    if (row.length > headerWidth) {
      errors.push("Row has more fields than the header row.");
    }
    const patient_name = get("patient_name");
    const patient_phone_raw = get("patient_phone");
    const address_line_1 = get("address_line_1");
    const address_line_2_raw = get("address_line_2");
    const city = get("city");
    const stateRaw = get("state");
    const zipRaw = get("zip");
    const order_number = get("order_number");
    const delivery_notes_raw = get("delivery_notes");
    const is_cold_chain_raw = get("is_cold_chain");
    if (isWhitespaceOnly(patient_name)) {
      errors.push("patient_name: required");
    }
    if (isWhitespaceOnly(patient_phone_raw)) {
      errors.push("patient_phone: required");
    } else {
      const e1642 = normalizeUsPhoneToE164(patient_phone_raw);
      if (e1642 === null) {
        errors.push("patient_phone: invalid US phone");
      }
    }
    if (isWhitespaceOnly(address_line_1)) {
      errors.push("address_line_1: required");
    }
    if (isWhitespaceOnly(city)) {
      errors.push("city: required");
    }
    if (isWhitespaceOnly(stateRaw)) {
      errors.push("state: required");
    } else {
      const state2 = stateRaw.toUpperCase();
      if (state2.length !== 2 || !US_STATE_CODES.has(state2)) {
        errors.push("state: not a 2-letter US state code");
      }
    }
    if (isWhitespaceOnly(zipRaw)) {
      errors.push("zip: required");
    } else if (!isValidZip(zipRaw)) {
      errors.push("zip: must be 5 digits or 5+4 with hyphen");
    }
    if (isWhitespaceOnly(order_number)) {
      errors.push("order_number: required");
    } else if (seenOrderNumbers.has(order_number)) {
      errors.push("order_number: duplicate within this file");
    } else {
      seenOrderNumbers.add(order_number);
    }
    let is_cold_chain = false;
    if (!isWhitespaceOnly(is_cold_chain_raw)) {
      const parsed2 = parseBoolCell(is_cold_chain_raw);
      if (parsed2 === null) {
        errors.push(
          "is_cold_chain: must be true/false, 1/0, t/f, yes/no, or blank"
        );
      } else {
        is_cold_chain = parsed2;
      }
    }
    if (errors.length > 0) {
      invalid.push({ row_number: rowNumber, errors });
      continue;
    }
    const e164 = normalizeUsPhoneToE164(patient_phone_raw);
    const state = stateRaw.toUpperCase();
    valid.push({
      patient_name: patient_name.trim(),
      patient_phone: e164,
      address_line_1: address_line_1.trim(),
      address_line_2: isWhitespaceOnly(address_line_2_raw) ? null : address_line_2_raw.trim(),
      city: city.trim(),
      state,
      zip: zipRaw.trim(),
      order_number: order_number.trim(),
      delivery_notes: isWhitespaceOnly(delivery_notes_raw) ? null : delivery_notes_raw.trim(),
      is_cold_chain
    });
  }
  if (valid.length === 0) {
    return {
      ok: false,
      file_errors: ["No valid data rows after validation (zero valid rows)."]
    };
  }
  return { ok: true, valid, invalid, warnings };
}

// src/orders/parse-trellis-csv.ts
import Papa2 from "papaparse";
var TRELLIS_REQUIRED_HEADERS = [
  "OrderID",
  "OriginName",
  "DestName",
  "DestAddress",
  "DestCity",
  "DestState",
  "DestZip",
  "DestPhone"
];
var TRELLIS_OPTIONAL_HEADERS = [
  "OriginAddress",
  "OriginCity",
  "OriginState",
  "OriginZip",
  "OriginPhone",
  "OriginComments",
  "Reference2",
  "DestComments",
  "SpecialInst",
  "DriverID"
];
var TRELLIS_KNOWN_HEADERS = /* @__PURE__ */ new Set([
  ...TRELLIS_REQUIRED_HEADERS,
  ...TRELLIS_OPTIONAL_HEADERS
]);
var COLD_CHAIN_RE = /^\s*cold\s*chain\b/i;
var LYNRX_CANONICAL_HEADER = "patient_name,patient_phone,address_line_1,address_line_2,city,state,zip,order_number,delivery_notes,is_cold_chain";
function decodeUtf8Strict2(input) {
  if (typeof input === "string") return input;
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(input);
  } catch {
    return null;
  }
}
function isWhitespaceOnly2(value) {
  return value === void 0 || value.trim() === "";
}
function firstNonEmptyLine2(text) {
  const lines = text.split(/\r?\n/);
  for (const line of lines) {
    if (line.trim() !== "") return line;
  }
  return null;
}
function nonCommaDelimiterError2(firstLine) {
  if (!/[\t|;]/.test(firstLine)) return null;
  const commaSeparatedParts = firstLine.split(",").length;
  if (commaSeparatedParts > 1) return null;
  return "CSV must use comma as the delimiter (tab, pipe, and semicolon-separated files are not supported).";
}
function buildTrellisHeaderIndex(headerCells) {
  const index = /* @__PURE__ */ new Map();
  const warnings = [];
  const extras = [];
  for (let c = 0; c < headerCells.length; c++) {
    const cell = headerCells[c].trim();
    if (cell === "") continue;
    if (!TRELLIS_KNOWN_HEADERS.has(cell)) {
      extras.push(cell);
      continue;
    }
    if (index.has(cell)) {
      return { ok: false, file_errors: [`Duplicate Trellis column in header: ${cell}`] };
    }
    index.set(cell, c);
  }
  if (extras.length > 0) {
    warnings.push(`Ignored unknown Trellis columns: ${extras.join(", ")}`);
  }
  const missing = TRELLIS_REQUIRED_HEADERS.filter((h) => !index.has(h));
  if (missing.length > 0) {
    return {
      ok: false,
      file_errors: [`Does not look like a Trellis CSV \u2014 missing required Trellis columns: ${missing.join(", ")}`]
    };
  }
  return { ok: true, index, warnings };
}
function csvEscape(value) {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}
function parseTrellisCsv(input, options = {}) {
  const text = decodeUtf8Strict2(input);
  if (text === null) {
    return { ok: false, file_errors: ["File is not valid UTF-8."] };
  }
  if (text.trim() === "") {
    return { ok: false, file_errors: ["CSV file is empty."] };
  }
  const firstLine = firstNonEmptyLine2(text);
  if (!firstLine) {
    return { ok: false, file_errors: ["CSV file is empty."] };
  }
  const delimErr = nonCommaDelimiterError2(firstLine);
  if (delimErr) {
    return { ok: false, file_errors: [delimErr] };
  }
  const parsed = Papa2.parse(text, {
    delimiter: ",",
    header: false,
    skipEmptyLines: false
  });
  const quoteErrors = parsed.errors.filter((e) => e.type === "Quotes").map((e) => e.message || "CSV could not be parsed (quoted field error).");
  if (quoteErrors.length > 0) {
    return { ok: false, file_errors: quoteErrors };
  }
  const rows = parsed.data;
  if (rows.length === 0) {
    return { ok: false, file_errors: ["CSV file is empty."] };
  }
  const headerCells = rows[0].map((cell) => (cell ?? "").trim());
  if (headerCells.every((h) => h === "")) {
    return { ok: false, file_errors: ["CSV header row is missing or empty."] };
  }
  const headerBuilt = buildTrellisHeaderIndex(headerCells);
  if (!headerBuilt.ok) {
    return { ok: false, file_errors: headerBuilt.file_errors };
  }
  const { index: col, warnings } = headerBuilt;
  const driverFilter = options.driver_id_filter?.trim() || null;
  const invalid = [];
  const extractsByOrigin = /* @__PURE__ */ new Map();
  let filteredOut = 0;
  for (let i = 1; i < rows.length; i++) {
    const rowNumber = i + 1;
    const row = rows[i];
    if (row.every((cell) => (cell ?? "").trim() === "")) {
      continue;
    }
    const get = (name) => {
      const idx = col.get(name);
      if (idx === void 0) return "";
      return (row[idx] ?? "").trim();
    };
    const driverId = get("DriverID");
    if (driverFilter !== null && driverId !== driverFilter) {
      filteredOut++;
      continue;
    }
    const errors = [];
    const orderId = get("OrderID");
    const originName = get("OriginName");
    const destName = get("DestName");
    const destAddress = get("DestAddress");
    const destCity = get("DestCity");
    const destState = get("DestState");
    const destZip = get("DestZip");
    const destPhone = get("DestPhone");
    if (isWhitespaceOnly2(orderId)) errors.push("OrderID: required");
    if (isWhitespaceOnly2(originName)) errors.push("OriginName: required");
    if (isWhitespaceOnly2(destName)) errors.push("DestName: required");
    if (isWhitespaceOnly2(destAddress)) errors.push("DestAddress: required");
    if (isWhitespaceOnly2(destCity)) errors.push("DestCity: required");
    if (isWhitespaceOnly2(destState)) errors.push("DestState: required");
    if (isWhitespaceOnly2(destZip)) errors.push("DestZip: required");
    if (isWhitespaceOnly2(destPhone)) errors.push("DestPhone: required");
    if (errors.length > 0) {
      invalid.push({ row_number: rowNumber, errors });
      continue;
    }
    const destComments = get("DestComments");
    const extract = {
      order_id: orderId,
      origin_name: originName,
      dest_name: destName,
      dest_address: destAddress,
      dest_city: destCity,
      dest_state: destState,
      dest_zip: destZip,
      dest_phone: destPhone,
      dest_comments: destComments,
      driver_id: driverId,
      is_cold_chain: COLD_CHAIN_RE.test(destComments)
    };
    const existing = extractsByOrigin.get(originName);
    if (existing) {
      existing.push(extract);
    } else {
      extractsByOrigin.set(originName, [extract]);
    }
  }
  const pharmacies = [];
  let totalRows = 0;
  for (const [originName, extracts] of extractsByOrigin) {
    const coldChainCount = extracts.reduce(
      (acc, e) => acc + (e.is_cold_chain ? 1 : 0),
      0
    );
    const csvLines = [LYNRX_CANONICAL_HEADER];
    for (const e of extracts) {
      csvLines.push(
        [
          csvEscape(e.dest_name),
          csvEscape(e.dest_phone),
          csvEscape(e.dest_address),
          "",
          csvEscape(e.dest_city),
          csvEscape(e.dest_state),
          csvEscape(e.dest_zip),
          csvEscape(e.order_id),
          csvEscape(e.dest_comments),
          e.is_cold_chain ? "true" : "false"
        ].join(",")
      );
    }
    pharmacies.push({
      origin_name: originName,
      row_count: extracts.length,
      cold_chain_count: coldChainCount,
      synthetic_csv: csvLines.join("\n")
    });
    totalRows += extracts.length;
  }
  pharmacies.sort((a, b) => a.origin_name.localeCompare(b.origin_name));
  return {
    ok: true,
    pharmacies,
    total_rows: totalRows,
    filtered_out: filteredOut,
    invalid,
    warnings
  };
}

// src/orders/parse-cnc-driver-route-csv.ts
import Papa3 from "papaparse";
var CNC_REQUIRED_HEADERS = [
  "Order ID",
  "Patient",
  "Address",
  "City",
  "ZIP",
  "Pharmacy"
];
var CNC_OPTIONAL_HEADERS = [
  "#",
  "Cold Chain",
  "Signature",
  "Status",
  "Notes"
];
var CNC_KNOWN_HEADERS = /* @__PURE__ */ new Set([
  ...CNC_REQUIRED_HEADERS,
  ...CNC_OPTIONAL_HEADERS
]);
var LYNRX_CANONICAL_HEADER2 = "patient_name,patient_phone,address_line_1,address_line_2,city,state,zip,order_number,delivery_notes,is_cold_chain";
var STUB_PHONE = "+15555550100";
var CNC_DEFAULT_STATE = "OH";
function decodeUtf8Strict3(input) {
  if (typeof input === "string") return input;
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(input);
  } catch {
    return null;
  }
}
function isWhitespaceOnly3(value) {
  return value === void 0 || value.trim() === "";
}
function firstNonEmptyLine3(text) {
  const lines = text.split(/\r?\n/);
  for (const line of lines) {
    if (line.trim() !== "") return line;
  }
  return null;
}
function nonCommaDelimiterError3(firstLine) {
  if (!/[\t|;]/.test(firstLine)) return null;
  const commaSeparatedParts = firstLine.split(",").length;
  if (commaSeparatedParts > 1) return null;
  return "CSV must use comma as the delimiter (tab, pipe, and semicolon-separated files are not supported).";
}
function buildCncHeaderIndex(headerCells) {
  const index = /* @__PURE__ */ new Map();
  const warnings = [];
  const extras = [];
  for (let c = 0; c < headerCells.length; c++) {
    const cell = headerCells[c].trim();
    if (cell === "") continue;
    if (!CNC_KNOWN_HEADERS.has(cell)) {
      extras.push(cell);
      continue;
    }
    if (index.has(cell)) {
      return { ok: false, file_errors: [`Duplicate CNC route column in header: ${cell}`] };
    }
    index.set(cell, c);
  }
  if (extras.length > 0) {
    warnings.push(`Ignored unknown CNC route columns: ${extras.join(", ")}`);
  }
  const missing = CNC_REQUIRED_HEADERS.filter((h) => !index.has(h));
  if (missing.length > 0) {
    return {
      ok: false,
      file_errors: [`Does not look like a CNC per-driver route CSV \u2014 missing required columns: ${missing.join(", ")}`]
    };
  }
  return { ok: true, index, warnings };
}
function csvEscape2(value) {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}
function parseCncRouteCsv(input) {
  const text = decodeUtf8Strict3(input);
  if (text === null) {
    return { ok: false, file_errors: ["File is not valid UTF-8."] };
  }
  if (text.trim() === "") {
    return { ok: false, file_errors: ["CSV file is empty."] };
  }
  const firstLine = firstNonEmptyLine3(text);
  if (!firstLine) {
    return { ok: false, file_errors: ["CSV file is empty."] };
  }
  const delimErr = nonCommaDelimiterError3(firstLine);
  if (delimErr) {
    return { ok: false, file_errors: [delimErr] };
  }
  const parsed = Papa3.parse(text, {
    delimiter: ",",
    header: false,
    skipEmptyLines: false
  });
  const quoteErrors = parsed.errors.filter((e) => e.type === "Quotes").map((e) => e.message || "CSV could not be parsed (quoted field error).");
  if (quoteErrors.length > 0) {
    return { ok: false, file_errors: quoteErrors };
  }
  const rows = parsed.data;
  if (rows.length === 0) {
    return { ok: false, file_errors: ["CSV file is empty."] };
  }
  const headerCells = rows[0].map((cell) => (cell ?? "").trim());
  if (headerCells.every((h) => h === "")) {
    return { ok: false, file_errors: ["CSV header row is missing or empty."] };
  }
  const headerBuilt = buildCncHeaderIndex(headerCells);
  if (!headerBuilt.ok) {
    return { ok: false, file_errors: headerBuilt.file_errors };
  }
  const { index: col, warnings } = headerBuilt;
  const invalid = [];
  const extractsByPharmacy = /* @__PURE__ */ new Map();
  for (let i = 1; i < rows.length; i++) {
    const rowNumber = i + 1;
    const row = rows[i];
    if (row.every((cell) => (cell ?? "").trim() === "")) {
      continue;
    }
    const get = (name) => {
      const idx = col.get(name);
      if (idx === void 0) return "";
      return (row[idx] ?? "").trim();
    };
    const errors = [];
    const orderId = get("Order ID");
    const patient = get("Patient");
    const address = get("Address");
    const city = get("City");
    const zip = get("ZIP");
    const pharmacy = get("Pharmacy");
    if (isWhitespaceOnly3(orderId)) errors.push("Order ID: required");
    if (isWhitespaceOnly3(patient)) errors.push("Patient: required");
    if (isWhitespaceOnly3(address)) errors.push("Address: required");
    if (isWhitespaceOnly3(city)) errors.push("City: required");
    if (isWhitespaceOnly3(zip)) errors.push("ZIP: required");
    if (isWhitespaceOnly3(pharmacy)) errors.push("Pharmacy: required");
    if (errors.length > 0) {
      invalid.push({ row_number: rowNumber, errors });
      continue;
    }
    const extract = {
      order_id: orderId,
      patient,
      address,
      city,
      zip,
      pharmacy,
      cold_chain: !isWhitespaceOnly3(get("Cold Chain")),
      notes: get("Notes")
    };
    const existing = extractsByPharmacy.get(pharmacy);
    if (existing) {
      existing.push(extract);
    } else {
      extractsByPharmacy.set(pharmacy, [extract]);
    }
  }
  const pharmacies = [];
  let totalRows = 0;
  for (const [pharmacyName, extracts] of extractsByPharmacy) {
    const coldChainCount = extracts.reduce(
      (acc, e) => acc + (e.cold_chain ? 1 : 0),
      0
    );
    const csvLines = [LYNRX_CANONICAL_HEADER2];
    for (const e of extracts) {
      csvLines.push(
        [
          csvEscape2(e.patient),
          STUB_PHONE,
          csvEscape2(e.address),
          "",
          csvEscape2(e.city),
          CNC_DEFAULT_STATE,
          csvEscape2(e.zip),
          csvEscape2(e.order_id),
          csvEscape2(e.notes),
          e.cold_chain ? "true" : "false"
        ].join(",")
      );
    }
    pharmacies.push({
      pharmacy_name: pharmacyName,
      row_count: extracts.length,
      cold_chain_count: coldChainCount,
      synthetic_csv: csvLines.join("\n")
    });
    totalRows += extracts.length;
  }
  pharmacies.sort((a, b) => a.pharmacy_name.localeCompare(b.pharmacy_name));
  return {
    ok: true,
    pharmacies,
    total_rows: totalRows,
    invalid,
    warnings
  };
}

// src/orders/backfill.ts
async function runPatientBackfill(deps) {
  const orders = await deps.fetchOrders();
  const summary = {
    total: orders.length,
    backfilled: 0,
    skipped: 0,
    errors: 0
  };
  for (const order of orders) {
    try {
      const fullNameEncrypted = await deps.encrypt(order.patient_name);
      if (!fullNameEncrypted) {
        deps.log?.({ order_id: order.id, status: "skipped_no_name" });
        summary.skipped += 1;
        continue;
      }
      const phoneEncrypted = await deps.encrypt(order.patient_phone);
      const patientId = await deps.callRpc({
        orderId: order.id,
        fullNameEncrypted,
        phoneEncrypted
      });
      deps.log?.({ order_id: order.id, status: "backfilled", patient_id: patientId });
      summary.backfilled += 1;
    } catch (err) {
      deps.log?.({
        order_id: order.id,
        status: "error",
        message: err instanceof Error ? err.message : String(err)
      });
      summary.errors += 1;
    }
  }
  return summary;
}

// src/sentry/phi-keys.ts
var PHI_KEYS = [
  // Identity
  "patient_name",
  "patientName",
  "first_name",
  "firstName",
  "last_name",
  "lastName",
  "full_name",
  "fullName",
  "name",
  // Encrypted-at-rest patient PHI (PR #61). Substring matching already
  // catches these via 'full_name'/'phone', but list them explicitly:
  // even ciphertext should never ride along in a Sentry payload.
  "patient_full_name_encrypted",
  "patient_phone_encrypted",
  "dob",
  "date_of_birth",
  "dateOfBirth",
  // Contact
  "email",
  "email_address",
  "emailAddress",
  "phone",
  "phone_number",
  "phoneNumber",
  "address",
  "street",
  "street_address",
  "streetAddress",
  "address_line_1",
  "address_line_2",
  "addressLine1",
  "addressLine2",
  "city",
  "zip",
  "zip_code",
  "zipCode",
  "postal_code",
  "postalCode",
  // Geo (address-derived; coarse city-level geo is fine, exact lat/lng is PHI by association)
  "latitude",
  "longitude",
  "lat",
  "lng",
  "gps",
  "coords",
  "coordinates",
  // Pharmacy-domain identifiers
  "order_number",
  "orderNumber",
  "pharmacy_order_number",
  "pharmacyOrderNumber",
  "rx_number",
  "rxNumber",
  "prescription_number",
  "prescriptionNumber",
  "medication_name",
  "medicationName",
  "medication",
  "drug_name",
  "drugName",
  // Photos / signatures (PHI by association)
  "photo_url",
  "photoUrl",
  "signature",
  "signature_url",
  "signatureUrl",
  "pod_photo",
  "podPhoto",
  // Health identifiers
  "ssn",
  "social_security_number",
  "mrn",
  "medical_record_number"
];
var PHI_REDACTION_PLACEHOLDER = "[PHI_REDACTED]";

// src/sentry/scrub.ts
var CIRCULAR = /* @__PURE__ */ Symbol("circular");
function keyMatchesPhi(key, phiKeys) {
  const lower = key.toLowerCase();
  for (const phi of phiKeys) {
    if (lower === phi.toLowerCase() || lower.includes(phi.toLowerCase())) {
      return true;
    }
  }
  return false;
}
function scrubValue(value, phiKeys = PHI_KEYS, seen = /* @__PURE__ */ new WeakSet(), depth = 0) {
  if (depth > 12) return CIRCULAR.toString();
  if (value === null || value === void 0) return value;
  if (typeof value !== "object") return value;
  if (seen.has(value)) return CIRCULAR.toString();
  seen.add(value);
  if (Array.isArray(value)) {
    return value.map((v) => scrubValue(v, phiKeys, seen, depth + 1));
  }
  const out = {};
  for (const [k, v] of Object.entries(value)) {
    if (keyMatchesPhi(k, phiKeys)) {
      out[k] = PHI_REDACTION_PLACEHOLDER;
    } else {
      out[k] = scrubValue(v, phiKeys, seen, depth + 1);
    }
  }
  return out;
}
function scrubSentryEvent(event, phiKeys = PHI_KEYS) {
  const scrubbed = { ...event };
  if (scrubbed.extra) {
    scrubbed.extra = scrubValue(scrubbed.extra, phiKeys);
  }
  if (scrubbed.contexts) {
    scrubbed.contexts = scrubValue(scrubbed.contexts, phiKeys);
  }
  if (scrubbed.tags) {
    scrubbed.tags = scrubValue(scrubbed.tags, phiKeys);
  }
  if (scrubbed.request) {
    scrubbed.request = scrubValue(scrubbed.request, phiKeys);
  }
  if (scrubbed.breadcrumbs && Array.isArray(scrubbed.breadcrumbs)) {
    scrubbed.breadcrumbs = scrubbed.breadcrumbs.map((b) => ({
      ...b,
      data: b.data ? scrubValue(b.data, phiKeys) : b.data
    }));
  }
  if (scrubbed.user) {
    scrubbed.user = scrubUserObject(scrubbed.user, phiKeys);
  }
  return scrubbed;
}
function scrubUserObject(user, phiKeys) {
  const safeKeys = /* @__PURE__ */ new Set(["id", "tenant_id", "user_type", "segment"]);
  const out = {};
  for (const [k, v] of Object.entries(user)) {
    if (safeKeys.has(k)) {
      out[k] = v;
    } else if (keyMatchesPhi(k, phiKeys)) {
      out[k] = PHI_REDACTION_PLACEHOLDER;
    } else {
      out[k] = scrubValue(v, phiKeys);
    }
  }
  return out;
}

// src/sentry/before-send.ts
function createBeforeSend(options = {}) {
  const phiKeys = options.extraPhiKeys ? [...PHI_KEYS, ...options.extraPhiKeys] : PHI_KEYS;
  const dropOnEmptyDsn = options.dropOnEmptyDsn ?? true;
  return function beforeSend(event) {
    if (dropOnEmptyDsn && !options.dsn) {
      return null;
    }
    return scrubSentryEvent(event, phiKeys);
  };
}

// src/supabase/client.ts
import {
  createClient
} from "@supabase/supabase-js";
function createSupabaseClient(url, key, options) {
  return createClient(url, key, options);
}

// src/crypto/encrypt.ts
import sodium from "libsodium-wrappers";

// src/crypto/key.ts
var KEY_BYTE_LENGTH = 32;
var cachedKey = null;
var PhiEncryptionKeyError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "PhiEncryptionKeyError";
  }
};
function decodeBase64(input) {
  if (typeof Buffer !== "undefined") {
    return new Uint8Array(Buffer.from(input, "base64"));
  }
  if (typeof atob === "function") {
    const binary = atob(input);
    const out = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
    return out;
  }
  throw new PhiEncryptionKeyError(
    "No base64 decoder available in this runtime"
  );
}
function getMasterKey() {
  if (cachedKey) return cachedKey;
  const raw = process.env["PHI_ENCRYPTION_KEY"];
  if (!raw || raw.length === 0) {
    throw new PhiEncryptionKeyError(
      "PHI_ENCRYPTION_KEY is not set. Generate with `node -e \"console.log(require('crypto').randomBytes(32).toString('base64'))\"` and set in Vercel + EAS."
    );
  }
  let bytes;
  try {
    bytes = decodeBase64(raw);
  } catch (err) {
    throw new PhiEncryptionKeyError(
      `PHI_ENCRYPTION_KEY is not valid base64: ${err.message}`
    );
  }
  if (bytes.length !== KEY_BYTE_LENGTH) {
    throw new PhiEncryptionKeyError(
      `PHI_ENCRYPTION_KEY must decode to ${KEY_BYTE_LENGTH} bytes (got ${bytes.length}). Generate a fresh key with crypto.randomBytes(32).toString('base64').`
    );
  }
  cachedKey = bytes;
  return cachedKey;
}

// src/crypto/encrypt.ts
var NONCE_BYTES = 24;
var readyPromise = null;
function ready() {
  if (!readyPromise) {
    readyPromise = sodium.ready;
  }
  return readyPromise;
}
var PhiDecryptError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "PhiDecryptError";
  }
};
async function encryptPHI(plaintext) {
  if (plaintext === null) return null;
  await ready();
  const key = getMasterKey();
  const nonce = sodium.randombytes_buf(NONCE_BYTES);
  const cipher = sodium.crypto_secretbox_easy(
    sodium.from_string(plaintext),
    nonce,
    key
  );
  const out = new Uint8Array(NONCE_BYTES + cipher.length);
  out.set(nonce, 0);
  out.set(cipher, NONCE_BYTES);
  return out;
}
async function decryptPHI(ciphertext) {
  if (ciphertext === null) return null;
  if (ciphertext.length <= NONCE_BYTES) {
    throw new PhiDecryptError(
      `ciphertext too short: ${ciphertext.length} bytes (need >${NONCE_BYTES})`
    );
  }
  await ready();
  const key = getMasterKey();
  const nonce = ciphertext.slice(0, NONCE_BYTES);
  const body = ciphertext.slice(NONCE_BYTES);
  let plain;
  try {
    plain = sodium.crypto_secretbox_open_easy(body, nonce, key);
  } catch (err) {
    throw new PhiDecryptError(
      `decryption failed: ${err.message ?? "auth failure"}`
    );
  }
  return sodium.to_string(plain);
}

// src/crypto/batch.ts
async function encryptPHIBatch(values) {
  return Promise.all(values.map((v) => encryptPHI(v)));
}
async function decryptPHIBatch(values) {
  return Promise.all(values.map((v) => decryptPHI(v)));
}

// src/crypto/sign.ts
import sodium2 from "libsodium-wrappers";
var readyPromise2 = null;
function ready2() {
  if (!readyPromise2) {
    readyPromise2 = sodium2.ready;
  }
  return readyPromise2;
}
var PdfSigningKeyError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "PdfSigningKeyError";
  }
};
var SECRET_KEY_BYTES = 64;
var PUBLIC_KEY_BYTES = 32;
function decodeBase642(input) {
  if (typeof Buffer !== "undefined") {
    return new Uint8Array(Buffer.from(input, "base64"));
  }
  if (typeof atob === "function") {
    const binary = atob(input);
    const out = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
    return out;
  }
  throw new PdfSigningKeyError("No base64 decoder available in this runtime");
}
var cachedSecret = null;
var cachedPublic = null;
function getSecretKey() {
  if (cachedSecret) return cachedSecret;
  const raw = process.env["PDF_SIGNING_SECRET_KEY"];
  if (!raw || raw.length === 0) {
    throw new PdfSigningKeyError(
      "PDF_SIGNING_SECRET_KEY is not set. Generate a keypair via libsodium `crypto_sign_keypair` and set both PDF_SIGNING_SECRET_KEY (base64 64-byte) and PDF_SIGNING_PUBLIC_KEY (base64 32-byte) in Vercel."
    );
  }
  const bytes = decodeBase642(raw);
  if (bytes.length !== SECRET_KEY_BYTES) {
    throw new PdfSigningKeyError(
      `PDF_SIGNING_SECRET_KEY decodes to ${bytes.length} bytes; Ed25519 requires ${SECRET_KEY_BYTES}.`
    );
  }
  cachedSecret = bytes;
  return bytes;
}
function getPublicKey() {
  if (cachedPublic) return cachedPublic;
  const raw = process.env["PDF_SIGNING_PUBLIC_KEY"];
  if (!raw || raw.length === 0) {
    throw new PdfSigningKeyError(
      "PDF_SIGNING_PUBLIC_KEY is not set. The verify endpoint needs the Ed25519 public key to check signatures."
    );
  }
  const bytes = decodeBase642(raw);
  if (bytes.length !== PUBLIC_KEY_BYTES) {
    throw new PdfSigningKeyError(
      `PDF_SIGNING_PUBLIC_KEY decodes to ${bytes.length} bytes; Ed25519 requires ${PUBLIC_KEY_BYTES}.`
    );
  }
  cachedPublic = bytes;
  return bytes;
}
async function signPdfBytes(bytes) {
  await ready2();
  const sk = getSecretKey();
  const sig = sodium2.crypto_sign_detached(bytes, sk);
  return Buffer.from(sig).toString("base64");
}
async function verifyPdfBytes(bytes, signatureBase64) {
  await ready2();
  const pk = getPublicKey();
  const sig = decodeBase642(signatureBase64);
  if (sig.length !== 64) {
    return false;
  }
  try {
    return sodium2.crypto_sign_verify_detached(sig, bytes, pk);
  } catch {
    return false;
  }
}
function _resetSigningKeyCacheForTests() {
  cachedSecret = null;
  cachedPublic = null;
}

// src/storage/paths.ts
var UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
var StoragePathError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "StoragePathError";
  }
};
function tenantPath(tenantId, ...segments) {
  if (!UUID_RE.test(tenantId)) {
    throw new StoragePathError(
      `tenantPath: tenantId must be a UUID (got ${JSON.stringify(tenantId)})`
    );
  }
  for (const seg of segments) {
    if (seg.length === 0) {
      throw new StoragePathError("tenantPath: empty segment");
    }
    if (seg.includes("/")) {
      throw new StoragePathError(
        `tenantPath: segment must not contain '/' (got ${JSON.stringify(seg)})`
      );
    }
  }
  return [tenantId, ...segments].join("/");
}

// src/geocoder/normalize.ts
var RULES = [
  /\s+(Apt|Apartment|Suite|Ste|Unit|Bldg|Building)\s+\S+$/i,
  /\s+#\s*\S+$/,
  /\s+(Floor|Fl)\s+\S+$/i
];
function normalizeStreetForGeocoder(street) {
  let result = street;
  let changed = true;
  while (changed) {
    changed = false;
    for (const rule of RULES) {
      const stripped = result.replace(rule, "");
      if (stripped !== result) {
        result = stripped;
        changed = true;
      }
    }
  }
  return result;
}

// src/geocoder/index.ts
var CENSUS_ONELINE_URL = "https://geocoding.geo.census.gov/geocoder/locations/onelineaddress";
var DEFAULT_TIMEOUT_MS = 5e3;
function joinAddress(input) {
  const street = normalizeStreetForGeocoder(input.street);
  return `${street}, ${input.city}, ${input.state} ${input.zip}`;
}
async function geocodeAddress(input, options = {}) {
  const doFetch = options.fetchImpl ?? fetch;
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const params = new URLSearchParams({
    address: joinAddress(input),
    benchmark: "Public_AR_Current",
    format: "json"
  });
  const url = `${CENSUS_ONELINE_URL}?${params.toString()}`;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await doFetch(url, {
      method: "GET",
      signal: controller.signal,
      // Census doesn't require auth headers. Setting Accept makes the
      // intent explicit; the service ignores Content-Type on GET.
      headers: { Accept: "application/json" }
    });
    if (!res.ok) return null;
    let body;
    try {
      body = await res.json();
    } catch {
      return null;
    }
    const match = body?.result?.addressMatches?.[0];
    if (!match) return null;
    const coords = match.coordinates;
    if (!coords) return null;
    const { x, y } = coords;
    if (typeof x !== "number" || typeof y !== "number") return null;
    if (Number.isNaN(x) || Number.isNaN(y)) return null;
    return { latitude: y, longitude: x };
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

// src/geofence/constants.ts
var GEOFENCE_RADIUS_METERS = 100;

// src/geofence/haversine.ts
function haversineMeters(aLat, aLng, bLat, bLng) {
  const R = 6371e3;
  const toRad = (d) => d * Math.PI / 180;
  const lat1 = toRad(aLat);
  const lat2 = toRad(bLat);
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const h = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  return R * c;
}

// src/routing/google-routes.ts
var ROUTES_ENDPOINT = "https://routes.googleapis.com/directions/v2:computeRoutes";
var FIELD_MASK = [
  "routes.distanceMeters",
  "routes.duration",
  "routes.optimizedIntermediateWaypointIndex"
].join(",");
var MAX_INTERMEDIATES = 25;
var MIN_INTERMEDIATES = 2;
function isFiniteCoord(v) {
  return typeof v === "number" && Number.isFinite(v);
}
function isValidLatLng(p) {
  return isFiniteCoord(p.latitude) && isFiniteCoord(p.longitude) && p.latitude >= -90 && p.latitude <= 90 && p.longitude >= -180 && p.longitude <= 180;
}
function toWaypoint(p) {
  return { location: { latLng: p } };
}
async function optimizeWaypoints(input) {
  if (!input.apiKey || input.apiKey.trim() === "") {
    return {
      ok: false,
      code: "api_key_missing",
      message: "Google Maps API key is not configured."
    };
  }
  const n = input.intermediates.length;
  if (n < MIN_INTERMEDIATES) {
    return {
      ok: false,
      code: "too_few_intermediates",
      message: `Need at least ${MIN_INTERMEDIATES} intermediates to optimize; got ${n}.`
    };
  }
  if (n > MAX_INTERMEDIATES) {
    return {
      ok: false,
      code: "too_many_intermediates",
      message: `Google Routes computeRoutes allows at most ${MAX_INTERMEDIATES} intermediates when optimizing; got ${n}.`
    };
  }
  if (!isValidLatLng(input.origin) || !isValidLatLng(input.destination)) {
    return {
      ok: false,
      code: "invalid_coordinate",
      message: "origin or destination has invalid lat/lng."
    };
  }
  for (let i = 0; i < input.intermediates.length; i++) {
    if (!isValidLatLng(input.intermediates[i])) {
      return {
        ok: false,
        code: "invalid_coordinate",
        message: `intermediates[${i}] has invalid lat/lng.`
      };
    }
  }
  const body = {
    origin: toWaypoint(input.origin),
    destination: toWaypoint(input.destination),
    intermediates: input.intermediates.map(toWaypoint),
    travelMode: "DRIVE",
    routingPreference: "TRAFFIC_AWARE",
    optimizeWaypointOrder: true
  };
  let res;
  try {
    const init = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Goog-Api-Key": input.apiKey,
        "X-Goog-FieldMask": FIELD_MASK
      },
      body: JSON.stringify(body)
    };
    if (input.signal) init.signal = input.signal;
    res = await fetch(ROUTES_ENDPOINT, init);
  } catch (err) {
    if (err.name === "AbortError") {
      return { ok: false, code: "aborted", message: "Google Routes call was aborted." };
    }
    return {
      ok: false,
      code: "http_error",
      message: `Google Routes fetch failed: ${err.message ?? "unknown"}`
    };
  }
  if (!res.ok) {
    return {
      ok: false,
      code: "http_error",
      message: `Google Routes returned HTTP ${res.status}`,
      http_status: res.status
    };
  }
  let parsed;
  try {
    parsed = await res.json();
  } catch {
    return {
      ok: false,
      code: "malformed_response",
      message: "Google Routes response was not valid JSON."
    };
  }
  const routes = parsed?.routes;
  if (!Array.isArray(routes) || routes.length === 0) {
    return {
      ok: false,
      code: "no_route",
      message: "Google Routes returned no routes (waypoints may be unreachable by driving)."
    };
  }
  const first = routes[0];
  const orderRaw = first.optimizedIntermediateWaypointIndex;
  if (!Array.isArray(orderRaw) || orderRaw.length !== n || !orderRaw.every((v) => Number.isInteger(v) && v >= 0 && v < n)) {
    return {
      ok: false,
      code: "malformed_response",
      message: "Google Routes response missing valid optimizedIntermediateWaypointIndex."
    };
  }
  const seen = /* @__PURE__ */ new Set();
  for (const idx of orderRaw) {
    if (seen.has(idx)) {
      return {
        ok: false,
        code: "malformed_response",
        message: "Google Routes returned duplicate indices in optimized order."
      };
    }
    seen.add(idx);
  }
  const distanceMeters = typeof first.distanceMeters === "number" ? first.distanceMeters : 0;
  const durationRaw = first.duration;
  let durationSeconds = 0;
  if (typeof durationRaw === "string") {
    const match = /^(\d+(?:\.\d+)?)s$/.exec(durationRaw);
    if (match) durationSeconds = Number(match[1]);
  }
  return {
    ok: true,
    optimized_order: orderRaw,
    distance_meters: distanceMeters,
    duration_seconds: durationSeconds
  };
}
export {
  GEOFENCE_RADIUS_METERS,
  MAX_INTERMEDIATES,
  MIN_INTERMEDIATES,
  PHI_KEYS,
  PHI_REDACTION_PLACEHOLDER,
  PdfSigningKeyError,
  PhiDecryptError,
  PhiEncryptionKeyError,
  StoragePathError,
  _resetSigningKeyCacheForTests,
  createBeforeSend,
  createSupabaseClient,
  ready as cryptoReady,
  decryptPHI,
  decryptPHIBatch,
  encryptPHI,
  encryptPHIBatch,
  geocodeAddress,
  haversineMeters,
  optimizeWaypoints,
  parseCncRouteCsv,
  parseOrdersCsv,
  parseTrellisCsv,
  runPatientBackfill,
  scrubSentryEvent,
  scrubValue,
  signPdfBytes,
  tenantPath,
  verifyPdfBytes
};
